import React from "react";
import "./ErrorBoundary.css";
import { captureException } from "@lib/sentry";
import { getApiErrorMessage } from "@lib/apis/apiErrorMessage";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError(error, info);
      } catch (err) {
        console.error("ErrorBoundary onError failed:", err);
      }
    }

    captureException(error, {
      contexts: {
        react_error_boundary: {
          boundaryName: this.props.boundaryName || this.props.name || "ErrorBoundary",
          componentStack: info?.componentStack || undefined,
          hasFallback: Boolean(this.props.fallback),
          resetKeysCount: Array.isArray(this.props.resetKeys) ? this.props.resetKeys.length : 0,
        },
      },
      tags: { boundary: this.props.boundaryName || this.props.name || "ErrorBoundary" },
    });
    console.error("ErrorBoundary caught an error", error, info);
  }

  componentDidUpdate(prevProps) {
    if (!this.state.hasError || !Array.isArray(this.props.resetKeys)) return;

    const resetChanged = this.props.resetKeys.some(
      (key, index) => key !== prevProps.resetKeys?.[index],
    );
    if (resetChanged) this.resetErrorBoundary();
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === "function") {
      try {
        this.props.onReset();
      } catch (error) {
        console.error("ErrorBoundary onReset failed:", error);
      }
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === "function") {
      return fallback({ error: this.state.error, resetErrorBoundary: this.resetErrorBoundary });
    }
    if (fallback) return fallback;

    return (
      <div className="error-boundary__fallback" role="alert">
        <h2>Something went wrong.</h2>
        <p>{getApiErrorMessage(
          { response: this.state.error?.response },
          "Please try again or contact support if the problem continues.",
        )}</p>
        <button type="button" onClick={this.resetErrorBoundary} className="error-boundary__retry">
          Try again
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
